import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';

const DropdownWrapper = ({
  toggleComponent,
  toggleClasses,
  onToggle,
  dropdownContent,
  classes,
  autoClose = true,
  align = 'start',
  mountTarget = 'body',
  fullWidth,
  disabled,
  zIndex,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [popperReady, setPopperReady] = useState(false);
  const [popperPositionUpdated, setPopperPositionUpdated] = useState(false);

  const menuRef = useRef(null);
  const popperRef = useRef(null);

  // When dropdown opens, keep popper instance reference
  const handleToggle = (isOpen, e, metadata) => {
    setDropdownOpen(isOpen);
    if (onToggle) {
      onToggle(isOpen);
    }
    if (metadata && metadata.popper) {
      popperRef.current = metadata.popper;
    }
    if (!isOpen) {
      setPopperReady(false);
      setPopperPositionUpdated(false);
    }
  };

  useEffect(() => {
    if (!dropdownOpen || !menuRef.current) return;
    const ro = new ResizeObserver(() => {
      if (popperRef.current) {
        popperRef.current.update();
        setPopperPositionUpdated(true);
      }
    });
    ro.observe(menuRef.current);
    return () => ro.disconnect();
  }, [dropdownOpen]);

  return (
    <div className={`${disabled ? 'pe-none opacity-50' : ''}`}>
      <Dropdown show={dropdownOpen} onToggle={handleToggle} autoClose={autoClose}>
        <Dropdown.Toggle variant='none' as='div' className={`${toggleClasses ?? ''}`}>
          {toggleComponent}
        </Dropdown.Toggle>

        {dropdownOpen && (
          <ConditionalDropdownMenu
            ref={menuRef}
            mountTarget={mountTarget}
            style={{
              zIndex: zIndex ?? 1045,
              visibility: dropdownOpen ? 'visible' : 'hidden',
              pointerEvents: dropdownOpen ? 'auto' : 'none',
            }}
            className={`dropdown-wrapper-menu user-select-none 
              ${popperReady && popperPositionUpdated ? 'popper-ready' : 'popper-hidden'} 
              ${classes ?? ''}`}
            align={align}
            popperConfig={{
              modifiers: [
                ...(fullWidth
                  ? [
                      {
                        name: 'sameWidth',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn: ({ state }) => {
                          state.styles.popper.width = `${state.rects.reference.width}px`;
                        },
                      },
                    ]
                  : []),
                {
                  name: 'flip',
                  options: {
                    boundary: 'viewport',
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    padding: 8,
                  },
                },
                {
                  name: 'computeStyles',
                  options: {
                    adaptive: false,
                  },
                },
                {
                  name: 'captureInstance',
                  enabled: true,
                  phase: 'write',
                  fn: ({ instance, state }) => {
                    popperRef.current = instance;
                    if (state.styles.popper.transform || state.styles.popper.top != null) {
                      setPopperReady(true);
                    }
                  },
                },
              ],
            }}
          >
            {typeof dropdownContent === 'function'
              ? dropdownContent({
                  handleCloseDropdown: () => {
                    setDropdownOpen(false);
                    setPopperReady(false);
                    setPopperPositionUpdated(false);
                  },
                })
              : dropdownContent}
          </ConditionalDropdownMenu>
        )}
      </Dropdown>
    </div>
  );
};

export default DropdownWrapper;

DropdownWrapper.propTypes = {
  toggleComponent: PropTypes.node.isRequired,
  toggleClasses: PropTypes.string,
  onToggle: PropTypes.func,
  dropdownContent: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  classes: PropTypes.string,
  autoClose: PropTypes.bool,
  align: PropTypes.string,
  mountTarget: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  zIndex: PropTypes.number,
};

const ConditionalDropdownMenu = React.forwardRef(({ mountTarget, children, ...props }, ref) => {
  const menu = (
    <Dropdown.Menu ref={ref} {...props}>
      {children}
    </Dropdown.Menu>
  );

  return mountTarget === 'body' ? createPortal(menu, document.body) : menu;
});

ConditionalDropdownMenu.displayName = 'ConditionalDropdownMenu';

ConditionalDropdownMenu.propTypes = {
  children: PropTypes.node,
  mountTarget: PropTypes.string,
};
